import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  TrendingUp, Clock, Camera, AtSign, Hash, MapPin, 
  Star, ChevronRight, Sparkles, Instagram, Eye,
  ThumbsUp, Repeat, CheckCircle2
} from 'lucide-react';

/**
 * TalentGrowthTips - "Get Booked More" section with actionable tips
 * 
 * Content:
 * - Best posting times by platform
 * - What to post after activations
 * - How to tag brands properly
 * - Content that gets you rebooked
 */

const TIP_CATEGORIES = [
  {
    id: 'posting-times',
    title: 'Best Times to Post',
    icon: Clock,
    color: 'cyan',
    tips: [
      { platform: 'Instagram', times: '9-11 AM & 6-8 PM', days: 'Tue-Thu', icon: Instagram },
      { platform: 'TikTok', times: '11 AM-1 PM & 7-10 PM', days: 'Tue, Wed, Fri' },
      { platform: 'LinkedIn', times: '8-10 AM', days: 'Tue-Thu' },
      { platform: 'X (Twitter)', times: '8-11 AM', days: 'Mon-Fri' },
    ],
    keyTip: 'Post within 2 hours of your event for maximum engagement'
  },
  {
    id: 'what-to-post',
    title: 'What to Post After Events',
    icon: Camera,
    color: 'pink',
    tips: [
      { text: 'Behind-the-scenes setup shots', icon: Camera },
      { text: 'Crowd interaction moments', icon: ThumbsUp },
      { text: 'Product/brand showcase', icon: Star },
      { text: 'Team photos (with permission)', icon: Repeat },
      { text: 'Event signage & branding', icon: Eye },
    ],
    keyTip: 'Brands love seeing their activations come to life through your lens'
  },
  {
    id: 'tagging',
    title: 'How to Tag Brands',
    icon: AtSign,
    color: 'amber',
    tips: [
      { text: '@tag the brand in your caption', correct: true },
      { text: '@tag the brand in your photo/video', correct: true },
      { text: 'Use their branded hashtags', correct: true },
      { text: 'Tag the venue location', correct: true },
      { text: 'Don\'t tag personal accounts', correct: false },
    ],
    keyTip: 'Always check if the brand has specific posting guidelines'
  },
  {
    id: 'rebook-content',
    title: 'Content That Gets You Rebooked',
    icon: Repeat,
    color: 'green',
    tips: [
      { text: 'Professional appearance & clean setup', icon: CheckCircle2 },
      { text: 'Engaging with attendees (genuine)', icon: ThumbsUp },
      { text: 'Showing product knowledge', icon: Star },
      { text: 'High-quality photos (good lighting!)', icon: Camera },
      { text: 'Positive, energetic captions', icon: Sparkles },
    ],
    keyTip: 'Brands review your social media before booking. Make it count!'
  }
];

const HASHTAG_GUIDE = {
  mustUse: ['#brandactivation', '#experientialmarketing', '#eventmarketing', '#brandambassador'],
  goodToUse: ['#eventstaff', '#eventlife', '#activationlife', '#brandexperience', '#liveevent'],
  avoidUsing: ['#ad', '#sponsored (unless required)', '#free', 'Unrelated trending tags']
};

const TipCard = ({ category, expanded, onToggle }) => {
  const Icon = category.icon;
  
  return (
    <Card className={`border-${category.color}-200 hover:border-${category.color}-300 transition-all cursor-pointer`}>
      <CardContent 
        className="p-3"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg bg-${category.color}-100 flex items-center justify-center`}>
              <Icon className={`w-4 h-4 text-${category.color}-600`} />
            </div>
            <div>
              <h4 className="font-medium text-gray-900 text-sm">{category.title}</h4>
              {!expanded && (
                <p className="text-xs text-gray-500 truncate max-w-[200px]">{category.keyTip}</p>
              )}
            </div>
          </div>
          <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
        
        {expanded && (
          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
            {category.id === 'posting-times' ? (
              <div className="space-y-2">
                {category.tips.map((tip, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 rounded-lg p-2">
                    <span className="font-medium text-gray-700">{tip.platform}</span>
                    <div className="text-right">
                      <span className="text-gray-600">{tip.times}</span>
                      <span className="text-gray-400 text-xs ml-2">({tip.days})</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : category.id === 'tagging' ? (
              <div className="space-y-1.5">
                {category.tips.map((tip, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    {tip.correct !== false ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    ) : (
                      <span className="w-4 h-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs shrink-0">✗</span>
                    )}
                    <span className={tip.correct === false ? 'text-gray-400' : 'text-gray-700'}>{tip.text}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5">
                {category.tips.map((tip, idx) => {
                  const TipIcon = tip.icon || CheckCircle2;
                  return (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <TipIcon className={`w-4 h-4 text-${category.color}-500 shrink-0`} />
                      <span>{tip.text}</span>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className={`mt-3 p-2 bg-${category.color}-50 rounded-lg border border-${category.color}-100`}>
              <p className={`text-xs text-${category.color}-700`}>
                💡 <strong>Key tip:</strong> {category.keyTip}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TalentGrowthTips = ({ compact = false }) => {
  const [expandedId, setExpandedId] = useState(null);
  const [showHashtags, setShowHashtags] = useState(false);
  
  const handleToggle = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };
  
  if (compact) {
    return (
      <Card className="border-teal-200 bg-gradient-to-br from-teal-50/30 to-white">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-teal-500" />
              <span className="font-medium text-gray-900 text-sm">Get Booked More</span>
            </div>
            <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200">
              4 tips
            </Badge>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Learn how to increase your visibility and get more bookings
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-teal-200 bg-gradient-to-br from-teal-50/20 to-white" data-testid="talent-growth-tips">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-teal-500" />
          Get Booked More
          <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200 ml-auto">
            Talent Growth Tips
          </Badge>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="pt-0 px-4 pb-4 space-y-3">
        {/* Tip Cards */}
        {TIP_CATEGORIES.map(category => (
          <TipCard
            key={category.id}
            category={category}
            expanded={expandedId === category.id}
            onToggle={() => handleToggle(category.id)}
          />
        ))}
        
        {/* Hashtag Quick Reference */}
        <Card className="border-violet-200">
          <CardContent 
            className="p-3 cursor-pointer"
            onClick={() => setShowHashtags(!showHashtags)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                  <Hash className="w-4 h-4 text-violet-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 text-sm">Hashtag Guide</h4>
                  <p className="text-xs text-gray-500">What to use & what to avoid</p>
                </div>
              </div>
              <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${showHashtags ? 'rotate-90' : ''}`} />
            </div>
            
            {showHashtags && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
                <div>
                  <p className="text-xs font-medium text-green-700 mb-1">✓ Must Use</p>
                  <div className="flex flex-wrap gap-1">
                    {HASHTAG_GUIDE.mustUse.map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-xs bg-green-50 text-green-700 rounded-full border border-green-200">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-cyan-700 mb-1">+ Good to Use</p>
                  <div className="flex flex-wrap gap-1">
                    {HASHTAG_GUIDE.goodToUse.map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-xs bg-cyan-50 text-cyan-700 rounded-full border border-cyan-200">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-red-700 mb-1">✗ Avoid</p>
                  <div className="flex flex-wrap gap-1">
                    {HASHTAG_GUIDE.avoidUsing.map(tag => (
                      <span key={tag} className="px-2 py-0.5 text-xs bg-red-50 text-red-600 rounded-full border border-red-200">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Call to Action */}
        <div className="text-center py-2 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-lg border border-teal-100">
          <p className="text-xs text-teal-700">
            🚀 Consistent posters get <strong>3x more bookings</strong>
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default TalentGrowthTips;
